/**
 * Main Plan Guide widget component.
 *
 * Phase 5: Enhanced with accessibility (ARIA) and responsive styling.
 *
 * @see frontend/workspace/CU-868gzzjua/frontend_research.md
 * @see frontend/workspace/CU-868gzzjua/frontend_implementation_guide.md
 */

import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { Box, Paper } from '@mui/material';
import { C1Component, ThemeProvider } from '@thesysai/genui-sdk';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { adaptMuiThemeToCrayon } from '@app/features/thesysChat/theme';

import { usePlanGuide } from '../hooks/usePlanGuide';
import { PlanGuideHeader } from './PlanGuideHeader';
import { PlanGuideLoading } from './PlanGuideLoading';
import { PlanGuideError } from './PlanGuideError';
import { C1ErrorBoundary } from './C1ErrorBoundary';
import type { PlanGuideProps, C1Action } from '../types';
import { PAGE_IDS } from '../constants';

export const PlanGuideWidget: React.FC<PlanGuideProps> = ({
  planResultRootId,
  planResultTypeId,
  onComplete,
  onClose,
}) => {
  const muiTheme = useMuiTheme();
  const crayonTheme = adaptMuiThemeToCrayon(muiTheme);
  const contentRef = useRef<HTMLDivElement>(null);

  const {
    pageId,
    title,
    c1Response,
    isLoading,
    error,
    isTerminal,
    backPage,
    session,
    fetchPage,
    handleAction,
    goBack,
    goToHub,
    clearState,
  } = usePlanGuide(planResultRootId, planResultTypeId);

  // Fetch initial page on mount
  useEffect(() => {
    fetchPage(PAGE_IDS.HUB);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle completion
  useEffect(() => {
    if (isTerminal && !isLoading) {
      onComplete?.();
    }
  }, [isTerminal, isLoading, onComplete]);

  // Focus management: move focus to content when page changes
  useEffect(() => {
    if (!isLoading && c1Response && contentRef.current) {
      // Announce page change to screen readers
      contentRef.current.focus();
    }
  }, [pageId, isLoading, c1Response]);

  // Handle C1 actions
  const onC1Action = useCallback(
    (action: C1Action) => {
      console.log('[PlanGuide] Action received:', action);
      handleAction(action);
    },
    [handleAction]
  );

  // Handle close with keyboard support
  const handleClose = useCallback(() => {
    clearState();
    onClose?.();
  }, [clearState, onClose]);

  // Handle Escape key to close
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose, onClose]);

  return (
    <Paper
      component="section"
      elevation={3}
      role="dialog"
      aria-modal="true"
      aria-label={`Plan Guide: ${title || 'Loading'}`}
      aria-busy={isLoading}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        // Responsive max-width for large screens
        maxWidth: { xs: '100%', lg: 1200 },
        mx: 'auto',
      }}
    >
      {/* Header with title, progress, and navigation */}
      <PlanGuideHeader
        title={title}
        currentPage={pageId}
        visitedPages={session?.visitedPages || []}
        canGoBack={!!backPage}
        onBack={goBack}
        onClose={handleClose}
      />

      {/* Main content area with focus management */}
      <Box
        ref={contentRef}
        component="main"
        tabIndex={-1}
        role="region"
        aria-label="Plan Guide Content"
        aria-live="polite"
        sx={{
          flex: 1,
          overflow: 'auto',
          // Responsive padding
          p: { xs: 2, sm: 3, md: 4 },
          // Ensure touch targets are accessible
          '& button, & [role="button"]': {
            minHeight: { xs: 44, sm: 'auto' },
            minWidth: { xs: 44, sm: 'auto' },
          },
          // Handle wide tables on mobile
          '& table': {
            display: 'block',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          },
          // Outline for focus visible
          '&:focus': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: `2px solid ${muiTheme.palette.primary.main}`,
            outlineOffset: -2,
          },
        }}
      >
        {/* Loading state */}
        {isLoading && <PlanGuideLoading />}

        {/* Error state */}
        {error && !isLoading && (
          <PlanGuideError
            error={error}
            onRetry={() => fetchPage(pageId)}
            onGoToHub={error.code === 404 ? goToHub : undefined}
          />
        )}

        {/* C1 Component render */}
        {c1Response && !isLoading && !error && (
          <ThemeProvider
            theme={crayonTheme.theme}
            darkTheme={crayonTheme.darkTheme}
            mode={crayonTheme.mode}
          >
            <C1ErrorBoundary
              onError={(err) =>
                console.error('[PlanGuide] C1 render error:', err)
              }
              onRetry={() => fetchPage(pageId)}
            >
              <C1Component
                c1Response={c1Response}
                isStreaming={false}
                onAction={onC1Action}
                onError={(err) => console.error('[PlanGuide] C1Component error:', err)}
              />
            </C1ErrorBoundary>
          </ThemeProvider>
        )}
      </Box>
    </Paper>
  );
};
